import type { PeerMetadata, SignalingGenerationV1 } from '@playcastdotio/Messaging';

export type HostInboundSignalKind = 'offer' | 'answer' | 'ice-candidate';

function valid(value: SignalingGenerationV1 | undefined): value is SignalingGenerationV1 {
  return value?.version === 1 && value.sessionId.trim().length > 0
    && Number.isInteger(value.epoch) && value.epoch >= 0;
}

/** Directional host guard: guest offers establish the generation that answers and ICE echo. */
export class HostSignalingGenerationManager {
  private readonly remoteByPeer = new Map<string, SignalingGenerationV1>();
  private readonly localByPeer = new Map<string, SignalingGenerationV1>();
  /**
   * Peers PROVEN to speak generations: set only by an offer that actually advertised the
   * capability. An answer or ICE candidate is judged against this, not against whichever
   * record happens to be present — the host's own `localByPeer` entry must never be what
   * makes a generation-less answer from a legacy peer look stale.
   */
  private readonly requiresGenerationByPeer = new Set<string>();

  public rememberLocalOffer(peerId: string, generation?: SignalingGenerationV1): boolean {
    if (!generation) {
      this.remoteByPeer.delete(peerId);
      this.localByPeer.delete(peerId);
      // A host offer with no envelope makes this a legacy exchange: the peer is no longer
      // required to speak generations.
      this.requiresGenerationByPeer.delete(peerId);
      return true;
    }
    if (!valid(generation)) return false;
    this.remoteByPeer.delete(peerId);
    this.localByPeer.set(peerId, { ...generation });
    return true;
  }

  public acceptInbound(peerId: string, kind: HostInboundSignalKind,
    generation?: SignalingGenerationV1, metadata?: PeerMetadata): boolean {
    if (kind === 'offer') {
      if (!generation) {
        if (metadata?.signalingGenerationVersion === 1) return false;
        this.localByPeer.delete(peerId);
        this.remoteByPeer.delete(peerId);
        this.requiresGenerationByPeer.delete(peerId);
        return true;
      }
      if (!valid(generation)) return false;
      const current = this.remoteByPeer.get(peerId);
      if (current?.sessionId === generation.sessionId && generation.epoch < current.epoch) return false;
      this.localByPeer.delete(peerId);
      this.remoteByPeer.set(peerId, { ...generation });
      // An offer that carries a well-formed envelope proves the peer speaks generations.
      this.requiresGenerationByPeer.add(peerId);
      return true;
    }

    // Answer / ICE. An ABSENT envelope is accepted unless the peer was PROVEN to speak
    // generations: that is what stops the host's own ICE-restart offer (which sets
    // `localByPeer`) from dropping a legacy guest's answer — the exact failure the review
    // found. A PRESENT envelope is still validated against the current record, so a
    // mismatched or superseded generation is rejected as before.
    if (generation === undefined) return !this.requiresGenerationByPeer.has(peerId);
    const current = this.remoteByPeer.get(peerId) ?? this.localByPeer.get(peerId);
    if (!current) return false;
    return valid(generation) && generation.sessionId === current.sessionId
      && generation.epoch === current.epoch;
  }

  /** True once an offer from this peer advertised the generation capability. */
  public requiresGeneration(peerId: string): boolean {
    return this.requiresGenerationByPeer.has(peerId);
  }

  public echoFor(peerId: string): SignalingGenerationV1 | undefined {
    const current = this.remoteByPeer.get(peerId);
    return current ? { ...current } : undefined;
  }

  public clear(peerId: string): void {
    this.remoteByPeer.delete(peerId);
    this.localByPeer.delete(peerId);
    this.requiresGenerationByPeer.delete(peerId);
  }

  public clearAll(): void {
    this.remoteByPeer.clear();
    this.localByPeer.clear();
    this.requiresGenerationByPeer.clear();
  }
}

